import {makeAutoObservable} from 'mobx';

export interface EnvironmentConfig {
    hlUrl: string;
    skillsApiUrl: string;
    catalogApiUrl: string;
    accreditationApiUrl: string;
    messagesApiUrl: string;
    ssoApiUrl: string;
    domSecManagementApiUrl: string;
    accessManagementApiUrl: string;
    academyDepartmentsUrl: string;
    planningIntegrationApiUrl: string;
    reportApiUrl: string;
    importApiUrl: string;
    notificationsApiUrl: string;
    apiUrl: string;
    authority: string;
}

class LeerplatformSettingsStore {
    private config: EnvironmentConfig = {
        hlUrl: '',
        skillsApiUrl: '',
        catalogApiUrl: '',
        accreditationApiUrl: '',
        messagesApiUrl: '',
        ssoApiUrl: '',
        domSecManagementApiUrl: '',
        accessManagementApiUrl: '',
        academyDepartmentsUrl: '',
        planningIntegrationApiUrl: '',
        reportApiUrl: '',
        importApiUrl: '',
        notificationsApiUrl: '',
        apiUrl: '',
        authority: '',
    };

    constructor() {
        makeAutoObservable(this);
    }

    // Getters
    get getHlUrl(): string {
        return this.config.hlUrl;
    }
    get getSkillsApiUrl(): string {
        return this.config.skillsApiUrl;
    }
    get getCatalogApiUrl(): string {
        return this.config.catalogApiUrl;
    }
    get getAccreditationApiUrl(): string {
        return this.config.accreditationApiUrl;
    }
    get getMessagesApiUrl(): string {
        return this.config.messagesApiUrl;
    }
    get getSsoApiUrl(): string {
        return this.config.ssoApiUrl;
    }
    get getDomSecManagementApiUrl(): string {
        return this.config.domSecManagementApiUrl;
    }
    get getAccessManagementApiUrl(): string {
        return this.config.accessManagementApiUrl;
    }
    get getAcademyDepartmentsUrl(): string {
        return this.config.academyDepartmentsUrl;
    }
    get getPlanningIntegrationApiUrl(): string {
        return this.config.planningIntegrationApiUrl;
    }
    get getReportApiUrl(): string {
        return this.config.reportApiUrl;
    }
    get getImportApiUrl(): string {
        return this.config.importApiUrl;
    }
    get getNotificationsApiUrl(): string {
        return this.config.notificationsApiUrl;
    }
    get getApiUrl(): string {
        return this.config.apiUrl;
    }
    get getAuthority(): string {
        return this.config.authority;
    }

    // Single setter for the entire config
    setConfig(newConfig: Partial<EnvironmentConfig>) {
        this.config = {...this.config, ...newConfig};
    }

    // environment.json bepaalt alle backend-URL's, dus alles hangt hieraan.
    // Mislukte dit, dan viel userManager terug op een configuratie met lege
    // URL's en liep de applicatie door met een onbruikbare staat: cryptische
    // fouten diep in de app in plaats van één duidelijke bovenaan.
    //
    // Het is een statisch bestand, dus een mislukking is bijna altijd tijdelijk
    // en een tweede poging kort erna helpt. Drie pogingen met 300 ms ertussen;
    // lukt het dan nog niet, dan gooit hij en is dat zichtbaar.
    async loadEnvironmentConfig(): Promise<EnvironmentConfig> {
        if (this.config.apiUrl) {
            return this.config;
        }

        let laatsteFout: unknown;

        for (let poging = 1; poging <= 3; poging++) {
            try {
                const response = await fetch('/environment.json');

                // Zonder deze controle liep een foutpagina door naar .json() en
                // werd de melding "Unexpected end of JSON input" in plaats van
                // de statuscode die zegt wat er werkelijk mis is.
                if (!response.ok) {
                    throw new Error(`environment.json gaf status ${response.status}`);
                }

                this.setConfig(await response.json());

                return this.config;
            } catch (error) {
                laatsteFout = error;

                if (poging < 3) {
                    await new Promise((resolve) => setTimeout(resolve, 300));
                }
            }
        }

        throw new Error(`environment.json kon na 3 pogingen niet geladen worden: ${laatsteFout}`);
    }
}

export const leerplatformSettingsStore = new LeerplatformSettingsStore();
